import * as React from "react";

import { AdvisorSentiment } from "@types";

import { cn } from "../cn";
import { ROW_STYLES } from "../sentiment";
import { SURFACE } from "../theme";

/**
 * The chrome every advisor table shares: shell, header row, cell padding, row borders and
 * sentiment tint. Cell *content* stays with each caller — these components own presentation,
 * not data.
 */

export type CellAlign = "left" | "right";

const TH =
  "whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500";
const TD = "px-4 py-2.5 text-gray-800";

export function DataTable({
  children,
  scroll = true,
  className,
}: {
  children: React.ReactNode;
  /** Wide tables scroll inside their own shell so the panel never scrolls sideways. */
  scroll?: boolean;
  className?: string;
}) {
  return (
    // min-w-0: as a flex item this shell won't shrink below its content width by default and can
    // push past the panel — min-w-0 lets it shrink normally.
    <div
      className={cn(
        SURFACE,
        "min-w-0",
        scroll ? "overflow-x-auto" : "overflow-hidden",
        className
      )}
    >
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function DataTableHead({
  columns,
  align,
}: {
  columns: string[];
  align?: (CellAlign | undefined)[];
}) {
  return (
    <thead>
      <tr className="border-b border-gray-200 bg-gray-100">
        {columns.map((column, i) => (
          <th
            key={i}
            className={cn(
              TH,
              align?.[i] === "right" ? "text-right" : "text-left"
            )}
          >
            {column}
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function DataTableRow({
  sentiment,
  className,
  children,
}: {
  sentiment?: AdvisorSentiment | null;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <tr
      className={cn(
        "border-b border-gray-200 last:border-b-0",
        sentiment ? ROW_STYLES[sentiment] : undefined,
        className
      )}
    >
      {children}
    </tr>
  );
}

export function DataTableCell({
  align = "left",
  wrap = false,
  className,
  children,
}: {
  align?: CellAlign;
  /** Prose cells wrap and top-align instead of widening the table into a scroll. */
  wrap?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      className={cn(
        TD,
        wrap ? "break-words align-top" : "whitespace-nowrap",
        align === "right" ? "text-right" : "text-left",
        className
      )}
    >
      {children}
    </td>
  );
}

/** Column alignment from the table's own numeric analysis — numbers right, everything else left. */
export function alignFromNumeric(numeric: boolean[]): CellAlign[] {
  return numeric.map(isNumeric => (isNumeric ? "right" : "left"));
}
